import { BASE_EPOCH } from './constants';
import { hexToBytes, detectTier } from './binary';
import { readText } from './decode';
import {
  EVENT_CATEGORIES, SEVERITY_LEVELS, STATUS_CODES, RADIUS_CODES,
  RESPONSE_FLAGS, RESOURCE_TYPES, RESOURCE_STATUS, TEXT_ENCODING,
} from './reference';

/**
 * One row of the byte inspector table — a single named field within an EMRG
 * payload, with its raw hex bytes and a human-readable decoded value.
 */
export interface ByteField {
  /** Byte offset from the start of the payload */
  offset: number;
  /** Length of this field in bytes */
  length: number;
  /** Human-readable field name (e.g. "Event Category") */
  name: string;
  /** Which structural block this field belongs to */
  block: 'schema' | 'envelope' | 'location' | 'event' | 'resource' | 'message' | 'crc';
  /** Raw hex of this field's bytes */
  hex: string;
  /** Decoded interpretation (e.g. "Fire", "Severity 3 (Warning)") */
  decoded: string;
}

const SENTINEL_GPS = 0x7FFFFFFF;

function sliceHex(bytes: Uint8Array, off: number, len: number): string {
  let s = '';
  for (let i = 0; i < len; i++) s += bytes[off + i]!.toString(16).padStart(2, '0').toUpperCase();
  return s;
}

function readUint(bytes: Uint8Array, off: number, len: number): number {
  let v = 0;
  for (let i = 0; i < len; i++) v = (v * 256) + bytes[off + i]!;
  return v;
}

function readInt32BE(bytes: Uint8Array, off: number): number {
  const u = readUint(bytes, off, 4);
  return u >= 0x80000000 ? u - 0x100000000 : u;
}

function readAscii(bytes: Uint8Array, off: number, len: number): string {
  let s = '';
  for (let i = 0; i < len; i++) {
    const c = bytes[off + i] ?? 0;
    if (c === 0) break;
    s += String.fromCharCode(c & 0x7F);
  }
  return s;
}

function messageText(bytes: Uint8Array, offset: number, length: number, encoding: number): string {
  try {
    return readText(bytes, offset, length, encoding) || '(empty)';
  } catch (error) {
    return `Invalid text: ${error instanceof Error ? error.message : 'invalid encoding'}`;
  }
}

function fmtTime(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

function decodeFlags(mask: number): string {
  const out: string[] = [];
  for (let bit = 15; bit >= 0; bit--) {
    if ((mask & (1 << bit)) !== 0) {
      const n = RESPONSE_FLAGS[bit];
      if (n && n !== 'Reserved') out.push(n);
    }
  }
  return out.length ? out.join(', ') : '(none)';
}

function categoryName(b: number): string {
  const code = b.toString(16).padStart(2, '0').toUpperCase();
  return `${EVENT_CATEGORIES[code] ?? 'Unknown'} (${code})`;
}

function statusName(b: number): string {
  const code = b.toString(16).padStart(2, '0').toUpperCase();
  return `${STATUS_CODES[code] ?? 'Unknown'} (${code})`;
}

function severityName(n: number): string {
  const s = SEVERITY_LEVELS[n];
  return s ? `${s.name} (${n})` : `Unknown (${n})`;
}

function radiusName(n: number): string {
  const r = RADIUS_CODES[n];
  return r ? `${r.name} (${n})` : `Unknown (${n})`;
}

function locFlagsDecoded(b: number): string {
  const parts: string[] = [];
  if (b & 0x80) parts.push('GPS');
  if (b & 0x40) parts.push('Alt');
  if (b & 0x20) parts.push('HiPrec');
  if (b & 0x10) parts.push('Motion');
  return parts.length ? parts.join(',') : '(none)';
}

function gpsDecoded(raw: number, axis: 'lat' | 'lng'): string {
  if (raw === SENTINEL_GPS) return 'no fix';
  return `${(raw / 1_000_000).toFixed(6)}° ${axis}`;
}

function priorityTtlDecoded(b: number): string {
  return `pri=${(b >> 4) & 0x0F} ttl=${b & 0x0F}`;
}

function resourceDecoded(typeByte: number, statusByte: number): string {
  if (typeByte === 0 && statusByte === 0) return '(empty)';
  const tCode = typeByte.toString(16).padStart(2, '0').toUpperCase();
  const tName = RESOURCE_TYPES[tCode] ?? 'Unknown';
  const sName = RESOURCE_STATUS[statusByte] ?? 'Unknown';
  return `${tName} · ${sName}`;
}

function pushField(out: ByteField[], bytes: Uint8Array, offset: number, length: number, name: string, block: ByteField['block'], decoded: string): void {
  out.push({ offset, length, name, block, hex: sliceHex(bytes, offset, length), decoded });
}

function inspectTier0(bytes: Uint8Array): ByteField[] {
  const f: ByteField[] = [];
  pushField(f, bytes, 0, 2, 'Schema Code', 'schema', `0x${sliceHex(bytes, 0, 2)} (EM)`);
  pushField(f, bytes, 2, 1, 'Subtype', 'schema', categoryName(bytes[2]!));
  const minOffset = (bytes[3]! << 16) | (bytes[4]! << 8) | bytes[5]!;
  pushField(f, bytes, 3, 3, 'Timestamp', 'envelope', fmtTime(BASE_EPOCH + minOffset * 60));
  pushField(f, bytes, 6, 2, 'Device ID', 'envelope', `0x${sliceHex(bytes, 6, 2)}`);
  pushField(f, bytes, 8, 1, 'Priority+TTL', 'envelope', priorityTtlDecoded(bytes[8]!));
  pushField(f, bytes, 9, 1, 'Location Flags', 'location', locFlagsDecoded(bytes[9]!));
  pushField(f, bytes, 10, 4, 'Latitude', 'location', gpsDecoded(readInt32BE(bytes, 10), 'lat'));
  pushField(f, bytes, 14, 4, 'Longitude', 'location', gpsDecoded(readInt32BE(bytes, 14), 'lng'));
  const sevStatus = bytes[18]!;
  pushField(f, bytes, 18, 1, 'Severity+Status', 'event', `${severityName((sevStatus >> 4) & 0x0F)} / ${statusName(sevStatus & 0x0F)}`);
  pushField(f, bytes, 19, 1, 'Radius', 'event', radiusName((bytes[19]! >> 4) & 0x0F));
  pushField(f, bytes, 20, 2, 'Response Flags', 'event', decodeFlags(readUint(bytes, 20, 2)));
  pushField(f, bytes, 22, 2, 'Message Ref', 'message', `0x${sliceHex(bytes, 22, 2)}`);
  pushField(f, bytes, 24, 25, 'Free Text', 'message', readAscii(bytes, 24, 25) || '(empty)');
  pushField(f, bytes, 49, 2, 'CRC16', 'crc', `0x${sliceHex(bytes, 49, 2)}`);
  return f;
}

function inspectTier12(bytes: Uint8Array, tier: 1 | 2): ByteField[] {
  const f: ByteField[] = [];
  pushField(f, bytes, 0, 5, 'Schema ID', 'schema', readAscii(bytes, 0, 5));
  const v = bytes[5]!;
  pushField(f, bytes, 5, 1, 'Version', 'schema', `v${(v >> 4) & 0x0F}.${v & 0x0F}`);
  pushField(f, bytes, 6, 1, 'Subtype', 'schema', categoryName(bytes[6]!));
  pushField(f, bytes, 7, 2, 'Sequence', 'envelope', readUint(bytes, 7, 2).toString());
  const tsOffset = readUint(bytes, 9, 4);
  pushField(f, bytes, 9, 4, 'Timestamp', 'envelope', fmtTime(BASE_EPOCH + tsOffset));
  pushField(f, bytes, 13, 1, 'Priority+TTL', 'envelope', priorityTtlDecoded(bytes[13]!));
  pushField(f, bytes, 14, 3, 'Device ID', 'envelope', `0x${sliceHex(bytes, 14, 3)}`);
  pushField(f, bytes, 17, 1, 'Location Flags', 'location', locFlagsDecoded(bytes[17]!));
  pushField(f, bytes, 18, 4, 'Latitude', 'location', gpsDecoded(readInt32BE(bytes, 18), 'lat'));
  pushField(f, bytes, 22, 4, 'Longitude', 'location', gpsDecoded(readInt32BE(bytes, 22), 'lng'));
  pushField(f, bytes, 26, 1, 'Event Category', 'event', categoryName(bytes[26]!));
  pushField(f, bytes, 27, 1, 'Event Severity', 'event', severityName(bytes[27]!));
  pushField(f, bytes, 28, 1, 'Event Status', 'event', statusName(bytes[28]!));
  pushField(f, bytes, 29, 1, 'Radius', 'event', radiusName(bytes[29]!));
  pushField(f, bytes, 30, 2, 'Response Flags', 'event', decodeFlags(readUint(bytes, 30, 2)));
  pushField(f, bytes, 32, 1, 'Resource Count', 'resource', bytes[32]!.toString());

  const slots = tier === 1 ? 5 : 10;
  for (let i = 0; i < slots; i++) {
    const off = 33 + i * 2;
    pushField(f, bytes, off, 2, `Resource[${i + 1}]`, 'resource',
      resourceDecoded(bytes[off]!, bytes[off + 1]!));
  }

  if (tier === 1) {
    pushField(f, bytes, 43, 1, 'Text Encoding', 'message', TEXT_ENCODING[bytes[43]!]?.name ?? `Unknown (${bytes[43]})`);
    pushField(f, bytes, 44, 2, 'Message Ref', 'message', `0x${sliceHex(bytes, 44, 2)}`);
    pushField(f, bytes, 46, 67, 'Free Text', 'message', messageText(bytes, 46, 67, bytes[43]!));
    pushField(f, bytes, 113, 2, 'CRC16', 'crc', `0x${sliceHex(bytes, 113, 2)}`);
  } else {
    pushField(f, bytes, 53, 1, 'Text Encoding', 'message', TEXT_ENCODING[bytes[53]!]?.name ?? `Unknown (${bytes[53]})`);
    pushField(f, bytes, 54, 2, 'Message Ref', 'message', `0x${sliceHex(bytes, 54, 2)}`);
    pushField(f, bytes, 56, 164, 'Free Text', 'message', messageText(bytes, 56, 164, bytes[53]!));
    pushField(f, bytes, 220, 2, 'CRC16', 'crc', `0x${sliceHex(bytes, 220, 2)}`);
  }

  return f;
}

/**
 * Walk an EMRG hex payload byte-by-byte and return a flat list of named
 * fields with their hex and decoded values. Powers the byte inspector table
 * in the dashboard and the decoder PWA.
 *
 * @param hex - Hex string to inspect
 * @returns Ordered list of fields covering every byte in the payload
 */
export function inspectBytes(hex: string): ByteField[] {
  try {
    const bytes = hexToBytes(hex);
    const tier = detectTier(bytes);
    return tier === 0 ? inspectTier0(bytes) : inspectTier12(bytes, tier);
  } catch {
    return [];
  }
}
